import { useMemo } from 'react';
import { ORIGENS_LEAD } from '../constants/origensLead';

const fmtBRL = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const PORTES = ['Pequeno', 'Médio', 'Grande', 'Enterprise'];
const PRAZOS = ['Imediato', '1–3 meses', '3–6 meses', '6–12 meses', '> 12 meses'];
const FAIXAS = [
  'Até R$ 2k/mês', 'R$ 2k–5k/mês', 'R$ 5k–10k/mês',
  'R$ 10k–20k/mês', 'Acima de R$ 20k/mês',
];

// BANT auto-score: 25pts each dimension
function calcBant(form) {
  const budget    = form.orcamento_confirmado    ? 25 : 0;
  const authority = form.decisor_identificado    ? 25 : 0;
  const need      = form.principal_dor?.trim()   ? 25 : 0;
  const timeline  = form.prazo_implantacao?.trim() ? 25 : 0;
  return { budget, authority, need, timeline, total: budget + authority + need + timeline };
}

function BantBar({ label, pts, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-[10px] text-gray-500 uppercase tracking-wide shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pts}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] tabular-nums font-semibold text-gray-600 w-8 text-right">{pts}pts</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold text-[#535c69] pb-1 border-b border-gray-100 w-full mb-2">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

export default function QualificacaoTab({ form, onChange }) {
  const ic = () =>
    `w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6]`;

  const bant = useMemo(() => calcBant(form), [
    form.orcamento_confirmado,
    form.decisor_identificado,
    form.principal_dor,
    form.prazo_implantacao,
  ]);

  // Manual override takes precedence when set; auto otherwise
  const displayScore = form.score_bant != null && form.score_bant !== '' ? Number(form.score_bant) : bant.total;
  const scoreColor = displayScore >= 70 ? '#22c55e' : displayScore >= 30 ? '#f59e0b' : '#ef4444';
  const scoreLabel = displayScore >= 70 ? 'Qualificado' : displayScore >= 30 ? 'Em qualificação' : 'Baixo';

  function check(field) {
    return (
      <input
        type="checkbox"
        checked={Boolean(form[field])}
        onChange={e => onChange(field, e.target.checked ? 1 : 0)}
        className="w-4 h-4 accent-[#2fc6f6] cursor-pointer"
      />
    );
  }

  return (
    <div className="px-6 py-4 space-y-5">

      {/* ── Empresa ─────────────────────────────────────────────────────── */}
      <Section title="Empresa">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Porte</label>
            <select value={form.porte || ''} onChange={e => onChange('porte', e.target.value)} className={ic('porte')}>
              <option value="">Selecione...</option>
              {PORTES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Usuários ERP</label>
            <input
              type="number" min="0"
              value={form.num_usuarios_erp ?? ''}
              onChange={e => onChange('num_usuarios_erp', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              className={ic('num_usuarios_erp')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">ERP Atual</label>
            <input
              type="text" placeholder="Ex: SAP, TOTVS RM, planilha..."
              value={form.erp_atual || ''}
              onChange={e => onChange('erp_atual', e.target.value)}
              className={ic('erp_atual')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tempo com ERP atual</label>
            <input
              type="text" placeholder="Ex: 3 anos, menos de 1 ano..."
              value={form.tempo_erp_atual || ''}
              onChange={e => onChange('tempo_erp_atual', e.target.value)}
              className={ic('tempo_erp_atual')}
            />
          </div>
        </div>
      </Section>

      {/* ── Dores / Necessidades ────────────────────────────────────────── */}
      <Section title="Dores e Necessidades">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Principal Dor</label>
          <textarea
            rows={2}
            placeholder="Descreva o problema central que o cliente quer resolver..."
            value={form.principal_dor || ''}
            onChange={e => onChange('principal_dor', e.target.value)}
            className={`${ic('principal_dor')} resize-none`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Módulos de Interesse</label>
          <input
            type="text" placeholder="Ex: Fiscal, PCP, Comercial..."
            value={form.modulos_interesse || ''}
            onChange={e => onChange('modulos_interesse', e.target.value)}
            className={ic('modulos_interesse')}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            {check('tem_ecommerce')}
            Tem e-commerce
          </label>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">NFs emitidas/mês</label>
            <input
              type="number" min="0"
              value={form.volume_nfs_mes ?? ''}
              onChange={e => onChange('volume_nfs_mes', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              className={ic('volume_nfs_mes')}
            />
          </div>
        </div>
      </Section>

      {/* ── Decisão ─────────────────────────────────────────────────────── */}
      <Section title="Decisão">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome do Decisor</label>
            <input
              type="text"
              value={form.nome_decisor || ''}
              onChange={e => onChange('nome_decisor', e.target.value)}
              className={ic('nome_decisor')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cargo do Decisor</label>
            <input
              type="text" placeholder="Ex: Diretor, Sócio, TI..."
              value={form.cargo_decisor || ''}
              onChange={e => onChange('cargo_decisor', e.target.value)}
              className={ic('cargo_decisor')}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            {check('decisor_identificado')}
            Decisor identificado
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            {check('tem_comite_compra')}
            Tem comitê de compra
          </label>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Prazo de Implantação</label>
          <select
            value={form.prazo_implantacao || ''}
            onChange={e => onChange('prazo_implantacao', e.target.value)}
            className={ic('prazo_implantacao')}
          >
            <option value="">Selecione...</option>
            {PRAZOS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </Section>

      {/* ── Budget ──────────────────────────────────────────────────────── */}
      <Section title="Budget">
        <div className="grid grid-cols-2 gap-3 items-start">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Faixa de Investimento</label>
            <select
              value={form.faixa_investimento || ''}
              onChange={e => onChange('faixa_investimento', e.target.value)}
              className={ic('faixa_investimento')}
            >
              <option value="">Selecione...</option>
              {FAIXAS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Origem do Lead</label>
            <select
              value={form.origem_lead || ''}
              onChange={e => onChange('origem_lead', e.target.value)}
              className={ic('origem_lead')}
            >
              <option value="">Selecione...</option>
              {ORIGENS_LEAD.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
          {check('orcamento_confirmado')}
          Orçamento confirmado pelo cliente
        </label>
      </Section>

      {/* ── Score BANT ──────────────────────────────────────────────────── */}
      <Section title="Score BANT">
        {/* Auto-score breakdown */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Score automático</span>
            <span className="text-xs text-gray-400">{bant.total}/100</span>
          </div>
          <BantBar label="Budget"    pts={bant.budget}    color="#A44DFF" />
          <BantBar label="Authority" pts={bant.authority} color="#00DBFF" />
          <BantBar label="Need"      pts={bant.need}      color="#00FFD4" />
          <BantBar label="Timeline"  pts={bant.timeline}  color="#f59e0b" />
        </div>

        {/* Score total (editable override) */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Score final
              <span className="ml-1 font-normal text-gray-400">(deixe vazio para usar auto)</span>
            </label>
            <input
              type="number" min="0" max="100"
              placeholder={String(bant.total)}
              value={form.score_bant ?? ''}
              onChange={e => onChange('score_bant', e.target.value === '' ? null : parseFloat(e.target.value))}
              className={ic('score_bant')}
            />
          </div>
          {/* Score badge */}
          <div className="flex flex-col items-center justify-center w-20 h-16 rounded-xl border-2 shrink-0"
               style={{ borderColor: scoreColor + '40', backgroundColor: scoreColor + '10' }}>
            <span className="text-xl font-bold tabular-nums" style={{ color: scoreColor }}>{displayScore}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: scoreColor }}>{scoreLabel}</span>
          </div>
        </div>

        {/* Observações */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Observações de Qualificação</label>
          <textarea
            rows={3}
            placeholder="Contexto adicional, riscos identificados, próximos passos..."
            value={form.obs_qualificacao || ''}
            onChange={e => onChange('obs_qualificacao', e.target.value)}
            className={`${ic('obs_qualificacao')} resize-none`}
          />
        </div>
      </Section>

      {/* ── ZOPA — Zona de Possível Acordo ──────────────────────────────── */}
      <ZopaSection form={form} onChange={onChange} ic={ic} />
    </div>
  );
}

function ZopaSection({ form, onChange, ic }) {
  const valorTeto = useMemo(
    () => (Number(form.valor_saas) || 0) + (Number(form.valor_adesao) || 0) + (Number(form.valor_servico) || 0),
    [form.valor_saas, form.valor_adesao, form.valor_servico]
  );

  const valorMinimo   = Number(form.valor_minimo_aceitavel) || 0;
  const descontoMaxPct = Number(form.desconto_maximo_pct) || 0;
  // Proposta após aplicar o desconto máximo autorizado
  const propostaComDesconto = valorTeto * (1 - descontoMaxPct / 100);

  // Posições percentuais sobre a régua (range: 0 → valorTeto)
  const minimoPercent   = valorTeto > 0 ? Math.min((valorMinimo / valorTeto) * 100, 100) : 0;
  const propostaPercent = valorTeto > 0 ? Math.min((propostaComDesconto / valorTeto) * 100, 100) : 0;

  const zopaViavel = propostaComDesconto >= valorMinimo;

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold text-[#535c69] pb-1 border-b border-gray-100 w-full mb-2">
        ZOPA — Negociação
      </legend>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Valor Mínimo Aceitável (R$)</label>
          <input
            type="number" step="0.01" min="0"
            placeholder="Ex: 8000"
            value={form.valor_minimo_aceitavel ?? ''}
            onChange={e => onChange('valor_minimo_aceitavel', e.target.value === '' ? null : parseFloat(e.target.value))}
            className={ic('valor_minimo_aceitavel')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Desconto Máximo Autorizado (%)</label>
          <input
            type="number" step="0.1" min="0" max="100"
            placeholder="Ex: 15"
            value={form.desconto_maximo_pct ?? ''}
            onChange={e => onChange('desconto_maximo_pct', e.target.value === '' ? null : parseFloat(e.target.value))}
            className={ic('desconto_maximo_pct')}
          />
        </div>
      </div>

      {/* Régua ZOPA */}
      {valorTeto > 0 && valorMinimo > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide">
            <span className="text-gray-400">Régua ZOPA</span>
            {zopaViavel
              ? <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">✓ Zona viável</span>
              : <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">⚠ Desconto excede mínimo</span>
            }
          </div>

          {/* Track */}
          <div className="relative mt-4 mb-6">
            <div className="flex h-3 rounded-full overflow-hidden">
              {/* Zona vermelha: 0 → mínimo */}
              <div className="bg-red-200" style={{ width: `${minimoPercent}%` }} />
              {/* Zona verde: mínimo → teto */}
              <div className="bg-green-200 flex-1" />
            </div>

            {/* Marcador: Mínimo */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full border-2 border-white shadow-sm"
              style={{ left: `calc(${minimoPercent}% - 6px)` }}
            />
            <div
              className="absolute top-5 text-[9px] font-semibold text-red-600 whitespace-nowrap -translate-x-1/2"
              style={{ left: `${minimoPercent}%` }}
            >
              Mínimo<br />{fmtBRL(valorMinimo)}
            </div>

            {/* Marcador: Proposta com desconto máx */}
            {descontoMaxPct > 0 && propostaPercent !== 100 && (
              <>
                <div
                  className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm ${zopaViavel ? 'bg-blue-500' : 'bg-red-600'}`}
                  style={{ left: `calc(${propostaPercent}% - 6px)` }}
                />
                <div
                  className={`absolute -top-7 text-[9px] font-semibold whitespace-nowrap -translate-x-1/2 ${zopaViavel ? 'text-blue-600' : 'text-red-600'}`}
                  style={{ left: `${propostaPercent}%` }}
                >
                  Proposta Atual<br />{fmtBRL(propostaComDesconto)}
                </div>
              </>
            )}

            {/* Marcador: Teto (100%) */}
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-green-600 rounded-full border-2 border-white shadow-sm right-0" />
            <div className="absolute top-5 text-[9px] font-semibold text-green-700 whitespace-nowrap right-0 translate-x-1/2">
              Teto<br />{fmtBRL(valorTeto)}
            </div>
          </div>

          {/* Legenda dos valores */}
          <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
            <div>
              <p className="text-[9px] uppercase tracking-wide text-gray-400 font-semibold">Mínimo</p>
              <p className="text-xs font-bold text-red-600">{fmtBRL(valorMinimo)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wide text-gray-400 font-semibold">
                {descontoMaxPct > 0 ? `Com ${descontoMaxPct}% desc.` : 'Proposta Atual'}
              </p>
              <p className={`text-xs font-bold ${zopaViavel ? 'text-blue-600' : 'text-red-600'}`}>
                {fmtBRL(propostaComDesconto)}
              </p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wide text-gray-400 font-semibold">Teto</p>
              <p className="text-xs font-bold text-green-700">{fmtBRL(valorTeto)}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Condições Especiais</label>
        <textarea
          rows={2}
          placeholder="Ex: treinamento gratuito, implantação subsidiada, migração inclusa..."
          value={form.condicao_especial || ''}
          onChange={e => onChange('condicao_especial', e.target.value)}
          className={`${ic('condicao_especial')} resize-none`}
        />
      </div>
    </fieldset>
  );
}
